import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { notification } from '@duxton-web/ui';

import { MainPageSkeleton } from '@/common/components';
import { RoleName } from '@/common/const/Auth';
import Service, {
  TechFamilyItem,
  TFRole,
  UserRoleScopeMapping,
} from '@/lib/App';
import { FormatErrorMessage } from '@/services/Format';
import {
  buildTechFamilyScopeAliasMap,
  normalizeLegacyRoleName,
  normalizeScope,
  PermissionUtils,
  TechFamilyScopeAliasMap,
  uniqueNormalizedScopes,
} from '@/utils/accessControl';
import { error } from '@/utils/logging';
import { getLoggedUserEmail } from '@/utils/user';

export type UserRolesContextType = {
  userTFRoles: TFRole[];
  userRoles: UserRoleScopeMapping[];
  errorMessage: string;
  enableServiceAuthZ: boolean;
  isAdmin: boolean;
  isInitialized: boolean;
  permissionUtils: PermissionUtils;
};

type UserRolesContextProviderProps = {
  children: ReactNode;
  enableServiceAuthZ: boolean;
};

const UserRolesContext = createContext<UserRolesContextType | undefined>(
  undefined,
);

type TechFamilyAliasItem = Partial<TechFamilyItem> & {
  prefix?: string;
};

const getTechFamilyAliasValues = (
  techFamily?: TechFamilyAliasItem | null,
): string[] => {
  if (!techFamily) return [];

  return uniqueNormalizedScopes([
    techFamily.name,
    techFamily.helix_name,
    techFamily.prefix,
  ]);
};

const techFamilyAliasMatchesScope = (
  scope: string,
  techFamily?: TechFamilyAliasItem | null,
): boolean => {
  const normalizedScope = normalizeScope(scope);

  return getTechFamilyAliasValues(techFamily).some(
    (alias) => normalizeScope(alias) === normalizedScope,
  );
};

const collectTechFamilyScopes = (
  userRoles: UserRoleScopeMapping[],
  userTFRoles: TFRole[],
): string[] =>
  uniqueNormalizedScopes([
    ...(userRoles ?? []).map((role) => role?.tf),
    ...(userTFRoles ?? []).map((role) => role?.tf),
  ]);

const fetchTechFamilyScopeAliases = async (
  scopes: string[],
): Promise<TechFamilyScopeAliasMap> => {
  const aliasGroups = await Promise.all(
    uniqueNormalizedScopes(scopes).map(async (scope) => {
      try {
        const techFamilies = await Service.planner!.getTechFamilies(scope);
        const safeTechFamilies = Array.isArray(techFamilies)
          ? techFamilies
          : [];
        const matchedTechFamilies = safeTechFamilies.filter((techFamily) =>
          techFamilyAliasMatchesScope(scope, techFamily),
        );

        return [
          scope,
          ...matchedTechFamilies.flatMap(getTechFamilyAliasValues),
        ];
      } catch (err) {
        error(
          err,
          'getTechFamilies failed while resolving user role tech-family aliases',
        );
        return [scope];
      }
    }),
  );

  return buildTechFamilyScopeAliasMap(aliasGroups);
};

const normalizeUserRoles = (
  roles: UserRoleScopeMapping[] | null | undefined,
): UserRoleScopeMapping[] =>
  Array.isArray(roles)
    ? roles.map((role) => {
        const tf = role?.tf ?? '';
        const policyNames = Array.isArray(role?.policyNames)
          ? role.policyNames
          : [];

        return {
          tf,
          roleName: normalizeLegacyRoleName(role?.roleName, {
            techFamily: tf,
            policyNames,
          }),
          service: role?.service ?? '',
          policyNames,
        };
      })
    : [];

const normalizeUserTFRoles = (roles: TFRole[] | null | undefined): TFRole[] =>
  Array.isArray(roles)
    ? roles.map((role) => ({
        tf: role?.tf ?? '',
        role: normalizeLegacyRoleName(role?.role, {
          techFamily: role?.tf,
        }),
      }))
    : [];

const deriveUserTFRoles = (roles: UserRoleScopeMapping[]): TFRole[] =>
  roles
    .filter(
      (role) => Boolean(role?.tf) || (role?.roleName ?? '') === RoleName.ADMIN,
    )
    .map((role) => ({
      tf: role?.tf ?? '',
      role: normalizeLegacyRoleName(role?.roleName, {
        techFamily: role?.tf,
        policyNames: role?.policyNames,
      }),
    }));

export const UserRolesContextProvider = ({
  children,
  enableServiceAuthZ,
}: UserRolesContextProviderProps) => {
  const [userTFRoles, setUserTFRoles] = useState<TFRole[]>([]);
  const [userRoles, setUserRoles] = useState<UserRoleScopeMapping[]>([]);
  const [techFamilyScopeAliases, setTechFamilyScopeAliases] =
    useState<TechFamilyScopeAliasMap>({});
  const [errorMessage, setErrorMessage] = useState('');
  const [isInitialized, setIsInitialized] = useState(false);

  const isAdmin = useMemo(() => {
    return (
      (userTFRoles ?? []).some((role) => role?.role === RoleName.ADMIN) ||
      (userRoles ?? []).some(
        (role) => (role?.roleName ?? '') === RoleName.ADMIN,
      )
    );
  }, [userTFRoles, userRoles]);

  const permissionUtils = useMemo(() => {
    return new PermissionUtils(
      userTFRoles ?? [],
      userRoles ?? [],
      enableServiceAuthZ,
      techFamilyScopeAliases,
    );
  }, [userTFRoles, userRoles, enableServiceAuthZ, techFamilyScopeAliases]);

  useEffect(() => {
    const userEmail = getLoggedUserEmail();

    const rolesPromise = enableServiceAuthZ
      ? Service.planner!.fetchUserRoles()
          .then(async (roles) => {
            const normalizedRoles = normalizeUserRoles(roles);
            const normalizedTFRoles = deriveUserTFRoles(normalizedRoles);
            const aliases = await fetchTechFamilyScopeAliases(
              collectTechFamilyScopes(normalizedRoles, normalizedTFRoles),
            );

            setUserRoles(normalizedRoles);
            setUserTFRoles(normalizedTFRoles);
            setTechFamilyScopeAliases(aliases);
          })
          .catch((err) => {
            // Concedo user-roles endpoint failed — fall back to legacy TF roles
            // so users are not locked out during Concedo outages.
            error(
              err,
              'fetchUserRoles failed, falling back to fetchUserTFRoles',
            );
            return Service.planner!.fetchUserTFRoles().then(async (roles) => {
              const normalizedTFRoles = normalizeUserTFRoles(roles);
              const aliases = await fetchTechFamilyScopeAliases(
                collectTechFamilyScopes([], normalizedTFRoles),
              );

              setUserRoles([]);
              setUserTFRoles(normalizedTFRoles);
              setTechFamilyScopeAliases(aliases);
            });
          })
      : Service.planner!.fetchUserTFRoles().then((roles) => {
          setUserRoles([]);
          setUserTFRoles(normalizeUserTFRoles(roles));
          setTechFamilyScopeAliases({});
        });

    rolesPromise
      .then(() => {
        setErrorMessage('');
      })
      .catch((err) => {
        const description = `Retrieving ACL roles failed, due to: ${FormatErrorMessage(err)}`;
        error(err, description);
        setErrorMessage(description);
        setUserRoles([]);
        setTechFamilyScopeAliases({});
        setUserTFRoles([]);
        notification.error({
          description: description,
          message: `Retrieving of ACL roles for user ${userEmail} failed`,
        });
        if (!enableServiceAuthZ) {
          Service.planner.logout();
        }
      })
      .finally(() => {
        setIsInitialized(true);
      });
  }, [enableServiceAuthZ]);

  const contextValue = useMemo(
    () => ({
      userTFRoles,
      userRoles,
      errorMessage,
      enableServiceAuthZ,
      isAdmin,
      isInitialized,
      permissionUtils,
    }),
    [
      userTFRoles,
      userRoles,
      errorMessage,
      enableServiceAuthZ,
      isAdmin,
      isInitialized,
      permissionUtils,
    ],
  );

  if (!isInitialized) {
    return <MainPageSkeleton />;
  }

  return (
    <UserRolesContext.Provider value={contextValue}>
      {children}
    </UserRolesContext.Provider>
  );
};

export const useUserRolesContext = () => {
  const context = useContext(UserRolesContext);
  if (!context) {
    error(
      new Error(
        'useUserRolesContext must be used within a UserRolesContextProvider',
      ),
    );
  }
  return context;
};

export const UserRolesContextConsumer = UserRolesContext.Consumer;
